import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Colours of the paper diorama in soft morning light, in the clear pastel
// afternoon halfway through, and at golden hour when the flow is finished.
// `light` and `shadow` tint the lit and shaded facets of folded paper.
const STOPS = {
  skyTop: ['#94C6E8', '#8EC8EE', '#EDB2A6'],
  skyMiddle: ['#CBE4F1', '#CBE9F6', '#F7C8A8'],
  skyHorizon: ['#FCE4D6', '#FAF0DA', '#FFE0AE'],
  farLit: ['#C6CBEB', '#C1D2EE', '#F4C3B4'],
  farShade: ['#9AA3D6', '#97AED9', '#C997A8'],
  midLit: ['#C3E2BE', '#BDE4B6', '#E9DDAA'],
  midShade: ['#92C2A3', '#8CC69A', '#C2B08E'],
  nearLit: ['#9DD2A0', '#97D598', '#CFD495'],
  nearShade: ['#6CAA83', '#64AE7A', '#A2A374'],
  bankLit: ['#88C38F', '#80C686', '#BFC886'],
  bankShade: ['#5B9A74', '#549E6E', '#8F9566'],
  riverLit: ['#B4DBF3', '#ACDAF5', '#DCD6F0'],
  riverShade: ['#7DB4E0', '#76B3E3', '#A9A3D8'],
  sun: ['#FFE3A8', '#FFEDBC', '#FFC067'],
  light: ['#FFF4E6', '#FFFCF2', '#FFEBC4'],
  shadow: ['#5B6A9C', '#5A799B', '#80506E'],
} as const;

type ColorName = keyof typeof STOPS;

export type OrigamiColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** The light over the diorama through the day (0 in the morning, 0.5 in the afternoon, 1 at golden hour). */
export const origamiColors = (daylight: number): OrigamiColors => {
  const scaled = clamp(daylight) * 2;
  const index = Math.min(1, Math.floor(scaled));
  const amount = smoothstep(0, 1, scaled - index);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as OrigamiColors,
  );
};

/** The paper sky stays pale from morning to golden hour, so the header text stays dark. */
export const origamiChromeTone = (progress: number): ChromeTone =>
  luminance(origamiColors(progress).skyTop) > 0.31 ? 'dark' : 'light';
